import "react";
import { useQuery } from "@tanstack/react-query";
import {
  Receipt,
  Download,
  ExternalLink,
  Loader2,
  FileText,
  Check,
  Clock,
  AlertCircle,
  Calendar,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { billingApi } from "@/services/api";
import { formatDate } from "@/utils/dateFormat";
import { generateUUID } from "@/utils/transform";

const STATUS_BADGES = {
  paid: { variant: "success", icon: Check, label: "Paid" },
  open: { variant: "warning", icon: Clock, label: "Open" },
  draft: { variant: "secondary", icon: FileText, label: "Draft" },
  uncollectible: {
    variant: "destructive",
    icon: AlertCircle,
    label: "Uncollectible",
  },
  void: { variant: "outline", icon: AlertCircle, label: "Void" },
};

export const Invoices = () => {
  const { t } = useTranslation();

  // ============================================
  // TanStack Query - Fetch Invoices Data
  // ============================================
  const { data: invoicesData, isLoading: loading } = useQuery({
    queryKey: ["billing-invoices"],
    queryFn: async () => {
      const [invoicesRes, upcomingRes] = await Promise.all([
        billingApi.getInvoices(50),
        billingApi.getUpcomingInvoice(),
      ]);
      return {
        invoices: invoicesRes.data.data || [],
        upcomingInvoice: upcomingRes.data.data,
      };
    },
    meta: {
      onError: () => {
        toast.error("Failed to load invoices");
      },
    },
  });

  const invoices = invoicesData?.invoices || [];
  const upcomingInvoice = invoicesData?.upcomingInvoice;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-100">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20 sm:pb-0">
      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">
            {t("billing.invoices.title")}
          </h2>
          <p className="text-muted-foreground">{t("billing.viewHistory")}</p>
        </div>
      </div>

      {/* Upcoming Invoice */}
      {upcomingInvoice && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Upcoming Invoice
                </CardTitle>
                <CardDescription>
                  Your next invoice will be generated on{" "}
                  {formatDate(upcomingInvoice.periodEnd)}
                </CardDescription>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold">
                  ${upcomingInvoice.amount.toFixed(2)}
                </div>
                <div className="text-sm text-muted-foreground">
                  {upcomingInvoice.currency}
                </div>
              </div>
            </div>
          </CardHeader>
          {upcomingInvoice.lines && upcomingInvoice.lines.length > 0 && (
            <CardContent>
              <div className="text-sm font-medium mb-2">
                {t("billing.lineItems")}
              </div>
              <div className="space-y-2">
                {upcomingInvoice.lines.map((line) => (
                  <div
                    key={generateUUID()}
                    className="flex justify-between text-sm"
                  >
                    <span className="text-muted-foreground">
                      {line.description}
                    </span>
                    <span>${line.amount.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Invoice History */}
      <Card>
        <CardHeader>
          <CardTitle>{t("admin.invoices.history")}</CardTitle>
          <CardDescription>All your past invoices and receipts</CardDescription>
        </CardHeader>
        <CardContent>
          {invoices.length === 0 ? (
            <div className="text-center py-12">
              <Receipt className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">
                {t("billing.noInvoicesYet")}
              </h3>
              <p className="text-muted-foreground">
                Your invoices will appear here once you subscribe to a paid
                plan.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("common.invoice")}</TableHead>
                  <TableHead>{t("common.date")}</TableHead>
                  <TableHead>{t("common.status")}</TableHead>
                  <TableHead className="text-right">
                    {t("common.amount")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("common.actions")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((invoice) => {
                  const statusConfig =
                    STATUS_BADGES[invoice.status] || STATUS_BADGES.open;
                  const StatusIcon = statusConfig.icon;

                  return (
                    <TableRow key={invoice.id}>
                      <TableCell>
                        <div className="font-medium">
                          {invoice.invoiceNumber || invoice.id}
                        </div>
                        {invoice.description && (
                          <div className="text-sm text-muted-foreground truncate max-w-50">
                            {invoice.description}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div>
                          {formatDate(invoice.createdAt)}
                        </div>
                        {invoice.paidAt && (
                          <div className="text-xs text-muted-foreground">
                            Paid {formatDate(invoice.paidAt)}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusConfig.variant}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {statusConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="font-medium">
                          ${invoice.amount.toFixed(2)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {invoice.currency}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {invoice.invoiceUrl && (
                            <Button variant="ghost" size="sm" asChild>
                              <a
                                href={invoice.invoiceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <ExternalLink className="h-4 w-4" />
                              </a>
                            </Button>
                          )}
                          {invoice.pdfUrl && (
                            <Button variant="ghost" size="sm" asChild>
                              <a
                                href={invoice.pdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <Download className="h-4 w-4" />
                              </a>
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Billing Info */}
      <Alert>
        <Receipt className="h-4 w-4" />
        <AlertDescription>
          Need to update your billing information or tax ID?{" "}
          <a href="/settings/billing" className="text-primary hover:underline">
            Go to Billing Settings
          </a>
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default Invoices;
